import { missionFilesRequireAnyOrgPermissions } from '../../../backend/features/mission-files/shared';
import { missionStatusSchema } from '../../../backend/features/mission/shared';
import { MissionFiles } from '../../../ui/features/mission-files/MissionFiles';
import { useAuthorizeManageMission } from '../../../ui/features/mission/hooks/useAuthorizeManageMission';
import { useMissionsTranslation } from '../../../ui/hooks';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { PageHeader } from '@juvare/common-web-ui/PageHeader';
import { usePermission } from '@juvare/contact-management/ui/hooks';
import { Box, Tab, Tabs } from '@mui/material';
import React, { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Messages } from '../message';
import { useMission } from '../mission/hooks/useMission';
import { useMissionTab } from '../mission/hooks/useMissionTab';
import { PastShifts } from '../shift-details/components/past-shifts';
import { UpcomingShifts } from '../shift-details/components/upcoming-shifts';
import { useShiftsByMissionId } from '../shift/hooks/useShiftsByMissionId';
import { MissionHeaderContent } from './components/MissionHeaderContent';
import { getDefaultRouteForMissionStatus } from './components/utils';
export const MissionDetailsPage = () => {
    var _a;
    const { t } = useMissionsTranslation();
    const { missionId } = useParams();
    const { missionTab, setMissionTab } = useMissionTab();
    const [searchParams, setSearchParams] = useSearchParams();
    const { requireAnySystemPermission, requireAnyOrgPermission } = usePermission();
    const { authorizeManageMission } = useAuthorizeManageMission();
    const isVolunteerView = missionTab === 'myMissions';
    const allowMissionChange = requireAnySystemPermission('manage-missions', 'manage-all-admin-missions');
    const allowViewFileTab = requireAnyOrgPermission(...missionFilesRequireAnyOrgPermissions);
    const { isLoading, upcomingShifts, pastShifts, fetchShifts, handleSubmitRequest, handleSelfWithdrawal, handleSendMessageToVolunteersAction, } = useShiftsByMissionId({
        missionId,
        isVolunteerView,
    });
    const { mission } = useMission({ missionId });
    const gradientType = useMemo(() => {
        switch (mission === null || mission === void 0 ? void 0 : mission.columns.status) {
            case 'mobilizing':
            case 'inProgress':
            case 'complete':
                return 'green-light';
            case 'closed':
                return 'black-dark-linear';
            case 'draft':
            default:
                return 'blue-light';
        }
    }, [mission === null || mission === void 0 ? void 0 : mission.columns.status]);
    if (!mission)
        return null;
    if (isLoading) {
        return React.createElement("div", null, t('common.loading'));
    }
    if (!isVolunteerView && !allowMissionChange) {
        setMissionTab('myMissions');
    }
    const missionTypeId = mission.columns.typeId;
    const statusResult = missionStatusSchema.safeParse(mission.columns.status);
    const missionStatus = statusResult.success ? statusResult.data : 'draft';
    const activeTab = (_a = searchParams.get('tab')) !== null && _a !== void 0 ? _a : getDefaultRouteForMissionStatus(missionStatus);
    const allowMessages = authorizeManageMission(missionTypeId, mission.columns.organizationSharing);
    const defaultRowsPerPage = 10;
    return (React.createElement(React.Fragment, null,
        React.createElement(PageHeader, { gradient: gradientType },
            React.createElement(MissionHeaderContent, { missionName: mission.columns.name, missionStatus: missionStatus, missionDescription: mission.data.description, contactName: mission.data.contactName, contactPhone: mission.data.contactPhone, contactEmail: mission.data.contactEmail, allowMissionChange: !isVolunteerView })),
        React.createElement(Box, { display: 'flex', flexDirection: 'column' },
            React.createElement(Box, { sx: {
                    pl: 4,
                    borderBottom: 1,
                    borderColor: themeColors.border.main,
                    boxShadow: 1,
                    zIndex: 1,
                } },
                React.createElement(Tabs, { variant: 'scrollable', value: activeTab, onChange: (_event, value) => {
                        setSearchParams((prevValues) => {
                            prevValues.set('tab', value);
                            return prevValues;
                        }, { replace: true });
                    } },
                    missionStatus !== 'closed' && (React.createElement(Tab, { label: t('missions.tabs.upcomingShifts'), value: 'upcomingshifts', "data-test": 'upcoming-shifts-tab' })),
                    React.createElement(Tab, { label: t('missions.tabs.pastShifts'), value: 'pastshifts', "data-test": 'past-shifts-tab' }),
                    allowMessages && React.createElement(Tab, { label: t('missions.tabs.messages'), value: 'messages', "data-test": 'messages' }),
                    allowViewFileTab && React.createElement(Tab, { label: t('missions.file.title'), value: 'files', "data-test": 'files-tab' }))),
            React.createElement(Box, { sx: {
                    px: { xs: 2, sm: 4 },
                    py: 4,
                    display: 'flex',
                    flexDirection: 'column',
                }, "data-test": 'shifts' },
                activeTab === 'upcomingshifts' && (React.createElement(UpcomingShifts, { shifts: upcomingShifts, missionId: missionId, missionStatus: missionStatus, missionTypeId: missionTypeId, missionOrganizationSharing: mission.columns.organizationSharing, isVolunteerView: isVolunteerView, onFetchShifts: fetchShifts, onRequestParticipation: handleSubmitRequest, onSelfWithdrawal: handleSelfWithdrawal })),
                activeTab === 'pastshifts' && (React.createElement(PastShifts, { shifts: pastShifts, missionId: missionId, isVolunteerView: isVolunteerView, onFetchShifts: fetchShifts, onSendMessageToVolunteers: handleSendMessageToVolunteersAction, defaultRowsPerPage: defaultRowsPerPage })),
                activeTab === 'messages' && allowMessages && React.createElement(Messages, { missionId: missionId }),
                activeTab === 'files' && (React.createElement(MissionFiles, { missionId: missionId, missionTypeId: missionTypeId, missionOrganizationSharing: mission.columns.organizationSharing }))))));
};
